<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useCartStore } from '@/stores/cart'
import { useGodmodeSlots } from '~/composables/useGodmodeSlots'
import { formatDate } from '~/utils/dates'
import { logApiError } from '~/utils/logger'

interface AvailabilityDay {
  date: string
  available: number
  capacity: number
  weekTicketsSold: number
  dayTicketsSold: number
}

interface AvailabilityWeekTicket {
  ticket_type_id: string
  name: string
  available: number
  capacity: number
  weekTicketsSold: number
}

interface AvailabilityData {
  campId: string
  maxAttendees: number
  weekTicketsSold: number
  weekTicketAvailability: AvailabilityWeekTicket[]
  dayAvailability: AvailabilityDay[]
}

interface AvailabilitySummary {
  weekAvailable: boolean
  dayAvailable: boolean
  limited: boolean
  soldOut: boolean
}

interface AgeGroup {
  id: string
  name: string
  sort_order: number | null
}

interface Camp {
  id: string | number
  title: string
  description: string
  image: string
  price: number
  priceLabel?: string
  duration: string
  location: string
  category: string
  categories?: Array<{ id: string, name: string, slug: string, is_public: boolean }>
  rating: number
  ratingCount: number
  ageGroup: string
  ageGroupIds: string[]
  periode: string
  periodIds: string[]
  periodNames: string[]
  spotsLeft: number
  featured: boolean
  status?: string | null
  ticketSaleStartDate?: string
  start_date?: string
  end_date?: string
  startDate?: string
  endDate?: string
  period?: string
  refundPolicy?: string
  refund_policy?: string
  show_available_tickets?: boolean | null
}

interface CampFilters {
  category?: string
  ageGroup?: string
  period?: string
  search?: string
  query?: string
  location?: string
  startDate?: string
  endDate?: string
}

interface Props {
  filters?: CampFilters
}

const props = defineProps<Props>()

const { canManageCamps, canAccessAdmin, canManageFinance, hasAnyRole } = usePermissions()
const { getAgeGroups } = useAgeGroups()

const canViewConceptCamps = computed(() => canManageCamps.value || canAccessAdmin.value)

const canViewAvailabilityCounts = computed(() => {
  if (canManageCamps.value || canAccessAdmin.value || canManageFinance.value)
    return true

  return hasAnyRole(['admin', 'godmode', 'monitor', 'accountant', 'manager'])
})

const canShowAvailabilityCounts = (camp: Camp) => {
  return Boolean(camp.show_available_tickets) || canViewAvailabilityCounts.value
}

// Pagination
const itemsPerPage = ref(9)
const page = ref(1)

// Error state
const errorMessage = ref<string>('')
const availabilityByCampId = ref<Record<string, AvailabilitySummary>>({})
const ageGroupsData = ref<AgeGroup[]>([])

// Compute query params reactively
const queryParams = computed(() => {
  const params: Record<string, string> = {
  }

  if (!canViewConceptCamps.value)
    params.status = 'published'

  if (props.filters?.query)
    params.search = props.filters.query

  // Use location_id if present (new system), fall back to location text (legacy)
  if (props.filters?.location && props.filters.location !== 'Alle Locaties' && props.filters.location !== '')
    params.location_id = props.filters.location

  return params
})

// Use useFetch with reactive query - this is the Nuxt 4 recommended pattern
// useFetch will automatically refetch when queryParams changes
const { data: campsResponse, status } = await useFetch<{ success: boolean, data: Camp[] }>('/api/camps', {
  query: queryParams,
  watch: [queryParams],
  lazy: true,
  server: false, // Client-only fetching since this depends on user filters
  onResponseError({ response }) {
    logApiError('/api/camps', response._data, { operation: 'fetchCamps', queryParams: queryParams.value })
    errorMessage.value = 'Er is een fout opgetreden bij het laden van de kampen.'
  },
})

// Computed camps from the response
const allCamps = computed(() => {
  if (campsResponse.value?.data)
    return campsResponse.value.data
  else if (campsResponse.value && Array.isArray(campsResponse.value))
    return campsResponse.value as Camp[]

  return []
})

// Loading state from useFetch status. The fetch is client-only (server: false), so on the server the status is
// 'idle': count that as loading too, otherwise the server renders the empty state ("Geen kampen gevonden")
// and the client (which starts as 'pending') reports a hydration mismatch.
const loading = computed(() => status.value === 'pending' || status.value === 'idle')

// Filtered camps based on search and filters
const filteredCamps = computed(() => {
  let camps = allCamps.value

  // Hide concept camps for non-admin users
  if (!canViewConceptCamps.value)
    camps = camps.filter(camp => camp.status !== 'draft')

  // Apply search query
  const searchQuery = props.filters?.query
  if (searchQuery) {
    camps = camps.filter(
      camp =>
        camp.title.toLowerCase().includes(searchQuery.toLowerCase())
        || camp.description.toLowerCase().includes(searchQuery.toLowerCase())
        || camp.category.toLowerCase().includes(searchQuery.toLowerCase()),
    )
  }

  // Location filter is handled server-side via location_id

  // Apply period filter (now using ID)
  const periodFilter = props.filters?.period
  if (periodFilter && periodFilter !== '')
    camps = camps.filter(camp => camp.periodIds?.includes(periodFilter))

  // Apply age group filter (now using ID)
  const ageGroupFilter = props.filters?.ageGroup
  if (ageGroupFilter && ageGroupFilter !== '')
    camps = camps.filter(camp => camp.ageGroupIds?.includes(ageGroupFilter))

  // Apply category filter
  const categoryFilter = props.filters?.category
  if (categoryFilter && categoryFilter !== 'Alle kampen')
    camps = camps.filter(camp => camp.category === categoryFilter)

  // Sort chronologically by start date (earliest first)
  camps = camps.sort((a, b) => {
    const dateA = new Date(a.start_date || a.startDate || '')
    const dateB = new Date(b.start_date || b.startDate || '')

    return dateA.getTime() - dateB.getTime()
  })

  return camps
})

const totalCamps = computed(() => filteredCamps.value.length)

const paginatedCamps = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return filteredCamps.value.slice(start, end)
})

const buildAvailabilitySummary = (data: AvailabilityData): AvailabilitySummary => {
  const weekAvailable = data.weekTicketAvailability.some(ticket => ticket.available > 0)
  const dayAvailable = data.dayAvailability.some(day => day.available > 0)

  return {
    weekAvailable,
    dayAvailable,
    limited: dayAvailable && !weekAvailable,
    soldOut: !dayAvailable && !weekAvailable,
  }
}

const loadAvailabilitySummaries = async (campsToCheck: Camp[]) => {
  const candidates = campsToCheck.filter(camp => camp.spotsLeft <= 0)
  const missing = candidates.filter(camp => !availabilityByCampId.value[String(camp.id)])

  if (!missing.length)
    return

  await Promise.all(missing.map(async camp => {
    try {
      const response = await $fetch<{ success: boolean, data: AvailabilityData }>(`/api/camps/${camp.id}/availability`)

      if (response.success) {
        availabilityByCampId.value = {
          ...availabilityByCampId.value,
          [String(camp.id)]: buildAvailabilitySummary(response.data),
        }
      }
    }
    catch (error) {
      logApiError(`/api/camps/${camp.id}/availability`, error)
    }
  }))
}

const getAvailabilitySummary = (campId: string | number) => availabilityByCampId.value[String(campId)]

const isLimitedAvailability = (camp: Camp) => getAvailabilitySummary(camp.id)?.limited ?? false

const isSoldOut = (camp: Camp) => {
  const summary = getAvailabilitySummary(camp.id)

  if (summary)
    return summary.soldOut

  return camp.spotsLeft <= 0
}

const resolveChipColor = (category: string) => {
  const colors: Record<string, string> = {
    'Sport': 'primary',
    'Kunst & Knutsel': 'success',
    'Avontuur': 'error',
    'Wetenschap': 'info',
    'Muziek': 'warning',
    'Natuur': 'success',
    'Technologie': 'secondary',
  }

  return colors[category] || 'primary'
}

const sortedAgeGroups = computed(() => {
  return [...ageGroupsData.value].sort((a, b) => {
    const aOrder = a.sort_order ?? Number.POSITIVE_INFINITY
    const bOrder = b.sort_order ?? Number.POSITIVE_INFINITY

    return aOrder - bOrder
  })
})

const resolveCampAgeLabel = (camp: Camp) => {
  if (!camp.ageGroupIds || camp.ageGroupIds.length === 0 || sortedAgeGroups.value.length === 0)
    return camp.ageGroup

  const selectedGroups = sortedAgeGroups.value.filter(group => camp.ageGroupIds.includes(group.id))

  if (selectedGroups.length === 0)
    return camp.ageGroup

  if (selectedGroups.length === 1)
    return selectedGroups.at(0)?.name || camp.ageGroup

  const first = selectedGroups.at(0)?.name
  const last = selectedGroups.at(-1)?.name

  if (!first || !last)
    return camp.ageGroup

  return `${first} tot en met ${last}`
}

// Check if a camp has public categories to display
const hasPublicCategory = (camp: Camp) => {
  // Hide if category is 'Algemeen' (default when no public categories exist)
  if (camp.category === 'Algemeen')
    return false

  // Hide if categories array exists but is empty
  if (camp.categories && camp.categories.length === 0)
    return false

  // Show if there's a valid category
  return camp.category && camp.category.trim() !== ''
}

watch(
  () => props.filters,
  () => {
    page.value = 1
  },
)

watch(
  () => paginatedCamps.value,
  camps => {
    loadAvailabilitySummaries(camps)
  },
  { immediate: true },
)

// Booking functionality
const cartStore = useCartStore()
const { resetSlots } = useGodmodeSlots()

const ticketSalesStarted = (camp: Camp) => {
  // Use server-computed flag when available (accounts for both start and end dates)
  if ('has_active_tickets' in camp)
    return (camp as Camp & { has_active_tickets: boolean }).has_active_tickets

  // Fallback: only check camp-level sale start date
  if (!camp.ticketSaleStartDate)
    return true

  const now = new Date()
  const saleStartDate = new Date(camp.ticketSaleStartDate)

  if (Number.isNaN(saleStartDate.getTime()))
    return true

  return now >= saleStartDate
}

const isTicketSaleClosed = (camp: Camp) => {
  // Only applies when tickets are known to be inactive (has_active_tickets computed server-side)
  if ((camp as any).has_active_tickets !== false)
    return false

  // Consider it closed only if the sale window already started at some point
  const saleStartStr = (camp as any).ticket_sale_start_date || camp.ticketSaleStartDate
  if (!saleStartStr)
    return false

  return new Date() >= new Date(saleStartStr)
}

const openBookingDialog = async (camp: Camp) => {
  const ageGroup = camp.ageGroup?.split('-')
  const campId = String(camp.id)

  cartStore.initCart(campId, camp.title, {
    location: camp.location,
    age_min: ageGroup?.[0] ? Number.parseInt(ageGroup[0].trim()) : undefined,
    age_max: ageGroup?.[1] ? Number.parseInt(ageGroup[1].trim()) : undefined,
    start_date: camp.start_date || camp.startDate,
    end_date: camp.end_date || camp.endDate,
    period: camp.period,
    refund_policy: camp.refund_policy || camp.refundPolicy,
  })
  resetSlots()
  cartStore.saveToLocalStorage()

  try {
    await navigateTo('/cart')
  }
  catch (error) {
    logApiError('navigateTo:/cart', error)
    window.location.href = '/cart'
  }
}

onMounted(async () => {
  try {
    const response = await getAgeGroups(true)

    ageGroupsData.value = (response.data || []) as AgeGroup[]
  }
  catch (error) {
    logApiError('/api/age-groups', error, { operation: 'loadAgeGroups' })
  }
})
</script>

<template>
  <div class="camps-listing">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center flex-wrap gap-4 mb-6">
      <div>
        <h3 class="text-h3 mb-1">
          Beschikbare kampen in de KampFabrik
        </h3>
      </div>

      <div class="d-flex align-center gap-4">
        <div class="text-h5">
          {{ totalCamps }} kampen gevonden
        </div>
      </div>
    </div>

    <!-- Error State -->
    <VAlert v-if="errorMessage" type="error" class="mb-6">
      {{ errorMessage }}
    </VAlert>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <VProgressCircular indeterminate color="primary" size="64" />
      <p class="text-body-1 mt-4">
        Kampen laden...
      </p>
    </div>

    <!-- Camps Grid -->
    <div v-else-if="paginatedCamps.length" class="mb-6">
      <VRow>
        <VCol v-for="camp in paginatedCamps" :key="camp.id" cols="12" md="4" sm="6">
          <VCard flat border class="h-100">
            <!-- Camp Image -->
            <div class="position-relative">
              <VImg
                v-if="camp.image"
                :src="camp.image || ''"
                height="200"
                cover
                class="cursor-pointer"
                @click="$router.push({ path: '/camp-details', query: { id: camp.id } })"
              />
              <div
                v-if="camp.image"
                v-show="false"
                class="camp-image-title-overlay"
              >
                <span class="camp-image-title-overlay__title text-center px-4">
                  {{ camp.title }}
                </span>
              </div>
              <div
                v-else
                class="camp-image-placeholder cursor-pointer d-flex align-center justify-center"
                @click="$router.push({ path: '/camp-details', query: { id: camp.id } })"
              >
                <span class="camp-image-placeholder__title text-center px-4">
                  {{ camp.title }}
                </span>
              </div>

              <!-- Top left: Category (only show if camp has public categories) -->
              <div
                v-if="hasPublicCategory(camp)"
                class="position-absolute ma-2"
                style="inset-block-start: 0; inset-inline-start: 0"
              >
                <VChip variant="elevated" :color="resolveChipColor(camp.category)" size="small">
                  {{ camp.category }}
                </VChip>
              </div>

              <div
                v-if="camp.status === 'draft' && canViewConceptCamps"
                class="position-absolute ma-2"
                style="inset-block-start: 0; inset-inline-end: 0"
              >
                <VChip variant="elevated" color="warning" size="small">
                  Concept
                </VChip>
              </div>

              <div
                v-else-if="camp.end_date && new Date(camp.end_date) < new Date() && canViewConceptCamps"
                class="position-absolute ma-2"
                style="inset-block-start: 0; inset-inline-end: 0"
              >
                <VChip variant="elevated" color="secondary" size="small">
                  Afgelopen
                </VChip>
              </div>

              <!-- Bottom badges -->
              <div
                v-if="camp.featured || camp.spotsLeft <= 5 || isLimitedAvailability(camp) || isSoldOut(camp)"
                class="position-absolute ma-2 d-flex gap-2"
                style="inset-block-end: 0; inset-inline-start: 0"
              >
                <VChip v-if="camp.featured" color="warning" size="small" variant="elevated">
                  <VIcon start icon="bx-star" size="16" />
                  Voorgesteld
                </VChip>
                <VChip v-if="isSoldOut(camp)" color="error" size="small" variant="elevated">
                  Volzet
                </VChip>
                <VChip v-else-if="isLimitedAvailability(camp)" color="warning" size="small" variant="elevated">
                  Beperkte beschikbaarheid
                </VChip>
                <VChip v-else-if="camp.spotsLeft <= 5" color="error" size="small" variant="elevated">
                  {{ canShowAvailabilityCounts(camp) ? `Nog ${camp.spotsLeft} plekken!` : 'Nog enkele plekken!' }}
                </VChip>
              </div>
            </div>

            <VCardText class="d-flex flex-column">
              <!-- Title & Description -->
              <h5
                class="text-h5 mb-2 cursor-pointer"
                @click="$router.push({ path: '/camp-details', query: { id: camp.id } })"
              >
                {{ camp.title }}
              </h5>
              <p class="text-body-2 mb-4 flex-grow-1">
                {{ camp.description }}
              </p>

              <!-- Camp Details -->
              <div class="d-flex flex-column gap-2 mb-4">
                <div class="d-flex align-center">
                  <VIcon icon="bx-calendar" size="18" class="me-2 text-medium-emphasis flex-shrink-0" />
                  <div class="d-flex flex-column">
                    <span v-if="camp.periodNames && camp.periodNames.length > 0" class="text-body-2 font-weight-medium">
                      {{ camp.periodNames.join(', ') }}
                    </span>
                    <span v-else class="text-body-2 font-weight-medium">
                      {{ camp.periode }}
                    </span>
                    <span class="text-caption text-disabled">
                      {{ formatDate(camp.start_date || camp.startDate || '') }} -
                      {{ formatDate(camp.end_date || camp.endDate || '') }}
                    </span>
                  </div>
                </div>
                <div class="d-flex align-center">
                  <VIcon icon="bx-time" size="18" class="me-2 text-medium-emphasis" />
                  <span class="text-body-2">{{ camp.duration }}</span>
                </div>
                <div class="d-flex align-center">
                  <VIcon icon="bx-map" size="18" class="me-2 text-medium-emphasis" />
                  <span class="text-body-2">{{ camp.location }}</span>
                </div>
                <div class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <VIcon icon="bx-group" size="18" class="me-2 text-medium-emphasis" />
                    <span class="text-body-2">{{ resolveCampAgeLabel(camp) }}</span>
                  </div>
                  <!-- Details Button -->
                  <VBtn
                    color="primary"
                    variant="tonal"
                    size="small"
                    @click="$router.push({ path: '/camp-details', query: { id: camp.id } })"
                  >
                    <VIcon start icon="bx-right-arrow-alt" size="20" />
                    Details
                  </VBtn>
                </div>
              </div>

              <VDivider class="mb-4" />

              <!-- Footer: Price and Boek Nu Button -->
              <div class="d-flex justify-space-between align-center gap-2">
                <!-- Price -->
                <div class="d-flex flex-column">
                  <span class="text-caption text-medium-emphasis">Vanaf</span>
                  <span class="text-h6 font-weight-bold" :class="camp.price === 0 ? 'text-error' : 'text-primary'">
                    {{ camp.price === 0 ? 'Volzet' : `€${camp.price}${camp.priceLabel || ''}` }}
                  </span>
                </div>

                <!-- Boek Nu Button -->
                <VBtn v-if="ticketSalesStarted(camp)" color="primary" @click="openBookingDialog(camp)">
                  <VIcon start icon="bx-cart" />
                  Boek Nu
                </VBtn>
                <VBtn v-else-if="isTicketSaleClosed(camp)" color="secondary" variant="tonal" disabled>
                  Ticket verkoop gesloten
                </VBtn>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </div>

    <!-- No Results -->
    <div v-else-if="!loading" class="text-center py-12">
      <VIcon icon="bx-search-alt" size="64" class="text-medium-emphasis mb-4" />
      <h4 class="text-h4 mb-2">
        Geen kampen gevonden
      </h4>
      <p class="text-body-1 text-medium-emphasis">
        Probeer uw zoekopdracht of filters aan te passen om te vinden wat u zoekt.
      </p>
    </div>

    <!-- Pagination -->
    <VPagination
      v-if="totalCamps > itemsPerPage"
      v-model="page"
      :length="Math.ceil(totalCamps / itemsPerPage)"
      active-color="primary"
      first-icon="bx-chevrons-left"
      last-icon="bx-chevrons-right"
      show-first-last-page
    />
  </div>
</template>

<style lang="scss" scoped>
.camps-listing {
  font-family: 'Peachy Kee JF', sans-serif;
}

.cursor-pointer {
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }
}

.text-h5.cursor-pointer {
  &:hover {
    color: rgb(var(--v-theme-primary));
    transform: none;
  }
}

// Reduce the large h3 header on small/mobile screens
@media (max-width: 599px) {
  .camps-listing {
    h3.text-h3 {
      font-size: 1.25rem !important;
      line-height: 1.2;
      margin-block-end: 0.5rem;
    }
  }
}

.camp-image-placeholder {
  background-color: rgb(var(--v-theme-primary));
  block-size: 200px;
  color: rgb(var(--v-theme-on-primary));
}

.camp-image-placeholder__title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  color: #fff;
  font-family: 'Peachy Keen JF', 'Public Sans', sans-serif !important;
  font-size: 1.5rem;
  font-weight: 600;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  line-height: 1.4;
  text-overflow: ellipsis;
}

.camp-image-title-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: #000;
  inset: 0;
  pointer-events: none;
}

.camp-image-title-overlay__title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  color: #000;
  font-family: 'Peachy Keen JF', 'Public Sans', sans-serif !important;
  font-size: 1.5rem;
  font-weight: 600;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  line-height: 1.3;
  text-align: center;
  text-overflow: ellipsis;
}
</style>
